import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import useSendOTP from '../../../hooks/queries/useSendOtp';
import { useOTPVerification } from '../../../hooks/queries/useOTPVerification';
import EnterOTPComponent from './EnterOTPComponent';
import { OTP_VALIDATION_SCHEMA } from '../../../yup/schemas';

const RESEND_TIMER_INITIAL = 60;

const EnterOTPContainer: React.FC = () => {
  const { state } = useLocation();
  const [timer, setTimer] = useState(RESEND_TIMER_INITIAL);
  const [isTimerActive, setTimerActive] = useState(true);
  const { isPending, sendOtpRequest: verifyOTP } = useOTPVerification();
  const { sendOtpRequest: sendOTP } = useSendOTP();

  const email = (state?.email as string) || '';

  const handleOTPSubmit = (data: { otpCode: string }) => {
    const { otpCode } = data || {};
    verifyOTP(email, Number(otpCode));
  };

  const { errors, values, handleSubmit, touched, setFieldValue, isValid } =
    useFormik({
      initialValues: { otpCode: '' },
      validationSchema: OTP_VALIDATION_SCHEMA,
      onSubmit: handleOTPSubmit,
      validateOnMount: true,
      validateOnChange: true,
    });

  const handleResetOTP = useCallback(() => {
    if (timer <= 0) {
      setTimer(RESEND_TIMER_INITIAL);
      sendOTP(email);
    }
  }, [timer, email, sendOTP]);

  const handleOTPChange = (value: string) => {
    setFieldValue('otpCode', value);
  };

  const isButtonDisabled = isPending || !isValid;

  useEffect(() => {
    if (timer <= 0) {
      setTimerActive(false);
      return;
    }

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  if (!email) {
    return <Navigate to="/" />;
  }

  return (
    <EnterOTPComponent
      email={email}
      timer={timer}
      isTimerActive={isTimerActive}
      isButtonDisabled={isButtonDisabled}
      handleResetOTP={handleResetOTP}
      handleOTPChange={handleOTPChange}
      handleSubmit={handleSubmit}
      values={values}
      touched={touched}
      errors={errors}
    />
  );
};

export default EnterOTPContainer;
